import { LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow } from "@mui/material";
import { StyledMainTableCell, StyledMainTableRow } from "../../../components/TableComponent";
import TablePaginationComponent from "../../../components/TablePagination";
import { TableLoader } from "../../../components/ContentLoader";
import styles from "../reports.module.css";

const renderCell = (column, row) => {
    if (column.render) return column.render(row);
    const value = row?.[column.key];
    return value === null || value === undefined || value === "" ? "-" : value;
};

/**
 * Column-driven, server-paginated report table. Renders a table on desktop
 * and stacked cards below 768px.
 *
 * columns: [{ key, label, align, render(row), exportValue(row), noExport, primary }]
 * The `primary` column is used as the card title on mobile.
 */
const ReportTable = ({ report, columns, emptyText = "No data found", minWidth = 650 }) => {
    if (report.initialLoading) {
        return <TableLoader />;
    }

    const { rows, total, loading, page, rowsPerPage, changePage, changeRowsPerPage } = report;
    const primaryColumn = columns.find((column) => column.primary) || columns[0];
    const otherColumns = columns.filter((column) => column !== primaryColumn);

    const pagination = (colSpan) => (<TablePaginationComponent
        count={total}
        colSpan={colSpan}
        page={page}
        rowsPerPage={rowsPerPage}
        handleChangePage={changePage}
        handleChangeRowsPerPage={changeRowsPerPage}
    />);

    return (<div className={`${styles.TableWrap} ${loading ? styles.IsLoading : ""}`}>
        <div className={`${styles.LoadingBar}`}>{loading && <LinearProgress color="inherit" />}</div>

        {/* Desktop table */}
        <div className={`${styles.CusTable}`}>
            <TableContainer component={Paper}>
                <Table sx={{ minWidth }} aria-label="Report table">
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <StyledMainTableCell key={column.key} align={column.align || "left"}>{column.label}</StyledMainTableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody className={`${styles.TableBody}`}>
                        {rows.map((row, index) => (
                            <StyledMainTableRow key={row?.id ?? index} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                {columns.map((column) => (
                                    <TableCell key={column.key} align={column.align || "left"}>{renderCell(column, row)}</TableCell>
                                ))}
                            </StyledMainTableRow>
                        ))}

                        {rows.length === 0 && (<TableRow>
                            <TableCell colSpan={columns.length} align="center" className={`${styles.EmptyCell}`}>
                                {loading ? "Loading..." : emptyText}
                            </TableCell>
                        </TableRow>)}
                    </TableBody>
                    {total > 0 && (<TableFooter>
                        <TableRow>
                            {pagination(columns.length)}
                        </TableRow>
                    </TableFooter>)}
                </Table>
            </TableContainer>
        </div>

        {/* Mobile stacked cards */}
        <div className={`${styles.MobileCardList}`}>
            {rows.map((row, index) => (
                <div className={`${styles.ReportCard}`} key={row?.id ?? index}>
                    <div className={`${styles.ReportCardHead}`}>
                        <span className={`${styles.ReportCardHeadLabel}`}>{primaryColumn.label}</span>
                        <span className={`${styles.ReportCardTitle}`}>{renderCell(primaryColumn, row)}</span>
                    </div>
                    <div className={`${styles.ReportCardBody}`}>
                        {otherColumns.map((column) => (
                            <p key={column.key}><span>{column.label}</span><strong>{renderCell(column, row)}</strong></p>
                        ))}
                    </div>
                </div>
            ))}
            {rows.length === 0 && <div className={`${styles.NoDataCard}`}>{loading ? "Loading..." : emptyText}</div>}
            {total > 0 && (<div className={`${styles.MobilePagination}`}>
                <Table>
                    <TableFooter>
                        <TableRow>
                            {pagination(1)}
                        </TableRow>
                    </TableFooter>
                </Table>
            </div>)}
        </div>
    </div>);
};

export default ReportTable;
